/**
 * Fit to Fly Certificate — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner name/address, patient details.
 * Manual fields: pet colour, vet name, vet license number.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatCalendarDate,
  formatSex,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { Spacer } from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface FitToFlyFields {
  colour: string
  vetName: string
  vetLicenseNr: string
}

export const defaultFitToFlyFields: FitToFlyFields = {
  colour: '',
  vetName: '',
  vetLicenseNr: '',
}

export function certifyText(species: string): string {
  return `This is to certify that I have examined the above mentioned ${species}. The animal shows no clinical signs of rabies or other infectious or contagious disease and is in my opinion fit to travel at the present time.`
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function FitToFlyPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: FitToFlyFields
}) {
  const { patient, owner, clinic } = context
  const address = [owner.address1, owner.address2, owner.city, owner.postalCode]
    .filter(Boolean)
    .join(', ')
  // Generated output with no viewer to follow — resolve from the owning member's region.
  const fmt = resolveDateFormat(null, owner.region)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>FIT TO FLY CERTIFICATE</Text>

        {/* Owner */}
        <Text style={styles.text}>
          {owner.firstName} {owner.lastName}
        </Text>
        {address && <Text style={styles.text}>{address}</Text>}

        {/* Patient details */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            Animal: <Text style={styles.bold}>{patient.name}</Text>
          </Text>
          <Text style={styles.text}>
            Species: <Text style={styles.bold}>{patient.species}</Text>
          </Text>
          <Text style={styles.text}>
            Breed: <Text style={styles.bold}>{patient.breed || '—'}</Text>
          </Text>
          {patient.dateOfBirth && (
            <Text style={styles.text}>
              DOB: <Text style={styles.bold}>{formatCalendarDate(patient.dateOfBirth, fmt)}</Text>
            </Text>
          )}
          <Text style={styles.text}>
            Sex: <Text style={styles.bold}>{formatSex(patient.sex, patient.isNeutered)}</Text>
          </Text>
          <Text style={styles.text}>
            Colour: <Text style={styles.bold}>{fields.colour || ' '}</Text>
          </Text>
          {patient.microchipNumber && (
            <Text style={styles.text}>
              Microchip Number: <Text style={styles.bold}>{patient.microchipNumber}</Text>
            </Text>
          )}
        </View>

        {/* Body */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>To whom it may concern,</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{certifyText(patient.species)}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            Please feel free to contact us for any further information or clarification.
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>Sincerely,</Text>
        </View>

        <VetSignoff
          vetName={fields.vetName}
          label="Dr"
          licenseNumber={fields.vetLicenseNr}
          showSignature
          showDate
        />

        <Spacer height={mm(6)} />
        <Text style={styles.text}>Snoots Vet, London</Text>
      </Page>
    </Document>
  )
}
